import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/SubscriptionItemsService';
import { SubscriptionItem } from '../types';

export const useSubscriptionItem = (subscriptionId: string, subscriptionItemId?: string) => {
    if (!subscriptionId) {
        throw new Error('subscriptionId is required parameter');
    }

    const queryClient = useQueryClient();

    const subscriptionItemQuery = useQuery({
        queryKey: ['subscription-item', subscriptionId, subscriptionItemId],
        queryFn: () => fetch(subscriptionItemId!),
        enabled: !!subscriptionItemId
    });

    const createMutation = useMutation({
        mutationFn: (values: SubscriptionItem) => {
            const { created, updated, ...rest } = values;
            return add({ ...rest, subscriptionId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscription-items', subscriptionId] });
            queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: SubscriptionItem) => {
            if (!subscriptionItemId) throw new Error('subscriptionItemId is required parameter');
            const { created, updated, ...rest } = values;
            return update(subscriptionItemId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscription-items', subscriptionId] });
            queryClient.invalidateQueries({ queryKey: ['subscription-item', subscriptionId, subscriptionItemId] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!subscriptionItemId) throw new Error('subscriptionItemId is required parameter');
            return deleteRecord(subscriptionItemId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscription-items', subscriptionId] });
        }
    });

    return {
        record: subscriptionItemQuery.data,
        loading: subscriptionItemQuery.isLoading,
        fetchRecord: subscriptionItemQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};